import { PlanOptionDto } from "@@@/dto/planSelection/PlanOptionDto";
import { GroupKey } from "@@@/types/GroupKey";

export class PlanOption {
    constructor(
        public readonly id: string,
        public readonly whmcsProductId: number | null,
        public readonly domain: string,
        public readonly groupKey: GroupKey | null,
        public readonly displayName: string,
        public readonly features: string[],
        public readonly prices: {
            monthly?: number;
            yearly?: number;
            oneTime?: number;
        },
        public readonly link: string | null,
        public readonly rationale: string | null,
        public readonly isPopular: boolean
    ) { }

    get price(): number {
        const { monthly, yearly, oneTime } = this.prices;
        if (typeof monthly === "number") {
            return monthly;
        }
        if (typeof yearly === "number") {
            return yearly;
        }
        if (typeof oneTime === "number") {
            return oneTime;
        }

        console.warn(`[PlanOption:price] ❌ Missing valid price for ${this.id}`, {
            prices: this.prices,
            plan: this
        });

        return 0;
    }

    get billingLabel(): string {
        if (this.prices.monthly !== undefined) return "monthly";
        if (this.prices.yearly !== undefined) return "yearly";
        if (this.prices.oneTime !== undefined) return "oneTime";

        console.warn(`[PlanOption:billingLabel] ❌ No billing label for ${this.id}`, this.prices);
        return "notAvailable";
    }

    get isMonthly(): boolean {
        return this.prices.monthly !== undefined;
    }

    get isYearly(): boolean {
        return this.prices.yearly !== undefined;
    }

    get isOneTime(): boolean {
        return this.prices.oneTime !== undefined;
    }

    static fromDto(dto: PlanOptionDto): PlanOption {
        if (typeof dto.price !== "number") {
            console.warn(`[PlanOption.fromDto] ❌ dto.price is not a number`, dto);
        }
        if (!["monthly", "yearly", "oneTime"].includes(dto.billingLabel)) {
            console.warn(`[PlanOption.fromDto] ⚠️ Unexpected billingLabel '${dto.billingLabel}' in dto`, dto);
        }

        const priceKey =
            dto.billingLabel === "monthly"
                ? "monthly"
                : dto.billingLabel === "yearly"
                    ? "yearly"
                    : "oneTime";

        const prices = {
            [priceKey]: dto.price,
        };

        return new PlanOption(
            dto.id,
            dto.whmcsProductId ?? null,
            dto.domain ?? "",
            dto.groupKey ?? null,
            dto.displayName,
            dto.features ?? [],
            prices,
            dto.link ?? null,
            dto.rationale ?? null,
            dto.isPopular ?? false
        );
    }

    static create(params: Partial<PlanOption> & Pick<PlanOption, "id" | "displayName" | "prices">): PlanOption {
        const hasValidPrice = Object.values(params.prices ?? {}).some(p => typeof p === "number");

        if (!hasValidPrice) {
            console.warn(`[PlanOption.create] ❌ No valid price in manual create() call`, params);
        }

        return new PlanOption(
            params.id,
            params.whmcsProductId ?? null,
            params.domain ?? "",
            params.groupKey ?? null,
            params.displayName,
            params.features ?? [],
            params.prices,
            params.link ?? null,
            params.rationale ?? null,
            params.isPopular ?? false
        );
    }
}
